import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from 'react-aria-components/Button';
import { resolveSurface, surfaceHref } from '../../app/surface';
import { SITE_NAME } from '../../app/site-config';
import { notificationCountGateway } from '../../features/notifications/notification-count-gateway';
import { useSession } from '../../features/session/SessionContext';
import { useAnnouncer } from '../accessibility/Announcer';
import { AccessibilityToolbar } from '../accessibility/AccessibilityToolbar';

interface MenuLink {
  label: string;
  href: string;
  current?: boolean;
}

// Todo apunta a rutas propias de este frontend (superficie "home"), nunca al
// sitio Jekyll viejo: ese sitio ya no es la fuente de estas páginas. "Inicio"
// y "Noticias" no están: la marca ya lleva a Inicio, y Noticias vive en la
// propia home — repetirlos acá era redundante.
const siteLinks: MenuLink[] = [
  { label: 'Actividades', href: surfaceHref('home', '/activities') },
  { label: 'Tutoriales', href: surfaceHref('home', '/post/tutoriales') },
  { label: 'Servicios', href: surfaceHref('home', '/post/web/servicios') },
];

export function SiteHeader() {
  const { state, signOut } = useSession();
  const { announce } = useAnnouncer();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const originRef = useRef<HTMLElement | null>(null);
  const currentSurface = resolveSurface(window.location.pathname);
  const authenticated = state.status === 'authenticated';

  const unreadCount = useQuery({
    queryKey: ['notification-count'],
    queryFn: () => notificationCountGateway.count(),
    enabled: authenticated && import.meta.env.MODE !== 'test',
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const unread = unreadCount.data ?? 0;

  const sessionLinks: MenuLink[] = authenticated
    ? [
        {
          label: unread > 0 ? `Notificaciones (${unread} sin leer)` : 'Notificaciones',
          href: surfaceHref('panel', '/notifications'),
          current: currentSurface === 'panel' && window.location.pathname.endsWith('/notifications'),
        },
        { label: 'Mi panel', href: surfaceHref('panel'), current: currentSurface === 'panel' },
      ]
    : state.status === 'checking'
      ? []
      : [{ label: 'Identifícate', href: surfaceHref('panel'), current: currentSurface === 'panel' }];

  const allLinks: MenuLink[] = [...siteLinks, ...sessionLinks];

  const menuItems = useCallback(
    () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []),
    [],
  );

  const focusItem = useCallback((index: number) => {
    const items = menuItems();
    if (!items.length) return;
    const next = (index + items.length) % items.length;
    setActiveIndex(next);
    items[next]?.focus();
  }, [menuItems]);

  const openMenu = useCallback(() => {
    setMenuOpen(true);
    announce('Menú desplegado.');
    window.setTimeout(() => focusItem(activeIndex), 0);
  }, [activeIndex, announce, focusItem]);

  const enterMenu = useCallback(() => {
    originRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (window.matchMedia('(min-width: 992px)').matches) {
      announce('Menú principal.');
      focusItem(activeIndex);
      return;
    }
    openMenu();
  }, [activeIndex, announce, focusItem, openMenu]);

  const closeMenu = useCallback((restoreFocus = true) => {
    setMenuOpen(false);
    announce('Menú plegado.');
    if (restoreFocus) window.setTimeout(() => toggleRef.current?.focus(), 0);
  }, [announce]);

  const exitMenu = useCallback(() => {
    if (!window.matchMedia('(min-width: 992px)').matches) {
      closeMenu();
      return;
    }
    announce('Saliste del menú.');
    const destination = originRef.current;
    originRef.current = null;
    if (destination && document.contains(destination) && !menuRef.current?.contains(destination)) destination.focus();
    else document.getElementById('main-content')?.focus();
  }, [announce, closeMenu]);

  useEffect(() => {
    const onShortcut = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.key.toLowerCase() !== 'm') return;
      event.preventDefault();
      if (menuOpen || menuRef.current?.contains(document.activeElement)) exitMenu();
      else enterMenu();
    };
    document.addEventListener('keydown', onShortcut);
    return () => document.removeEventListener('keydown', onShortcut);
  }, [enterMenu, exitMenu, menuOpen]);

  const handleMenuKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = menuItems();
    if (!items.length) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    const move = (next: number) => {
      event.preventDefault();
      focusItem(next);
    };

    if (event.key === 'Escape') {
      event.preventDefault();
      exitMenu();
    } else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(items.length - 1);
    else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') move(current + 1);
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') move(current - 1);
    else if (!event.altKey && !event.ctrlKey && !event.metaKey && event.key.length === 1 && /\S/.test(event.key)) {
      const letter = event.key.toLocaleLowerCase('es');
      for (let step = 1; step <= items.length; step += 1) {
        const index = (Math.max(current, -1) + step) % items.length;
        if ((items[index]?.textContent ?? '').trim().toLocaleLowerCase('es').startsWith(letter)) {
          move(index);
          break;
        }
      }
    }
  };

  const leave = async () => {
    setSigningOut(true);
    try {
      await signOut();
      announce('Sesión cerrada.');
      closeMenu(false);
    } catch (error) {
      console.error('[Club Accesible] No se pudo cerrar la sesión.', error);
      announce('No pudimos cerrar la sesión. Inténtalo de nuevo.');
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <header className="site-header">
      <nav className="navbar navbar-expand-lg site-nav" aria-label="Navegación principal">
        <div className="container">
          <a className="navbar-brand brand-mark" href={surfaceHref('home')}>
            <img src="/assets/logo/tecnologia-accesible.svg" alt="" width="48" height="48" />
            <span>{SITE_NAME}</span>
          </a>

          <div className="d-flex align-items-center gap-2 order-lg-3">
            <AccessibilityToolbar />
            <Button
              ref={toggleRef}
              className="navbar-toggler"
              aria-controls="navbarPrincipal"
              aria-expanded={menuOpen}
              aria-label={menuOpen ? 'Cerrar menú de navegación' : 'Abrir menú de navegación'}
              aria-haspopup="menu"
              aria-keyshortcuts="Alt+M"
              onPress={() => (menuOpen ? closeMenu() : openMenu())}
            >
              <span className="navbar-toggler-icon" />
            </Button>
          </div>

          <div
            ref={menuRef}
            id="navbarPrincipal"
            className={`collapse navbar-collapse${menuOpen ? ' show' : ''}`}
            aria-keyshortcuts="Alt+M"
            onKeyDown={handleMenuKey}
          >
            <ul className="navbar-nav ms-auto" role="menu" aria-label="Principal">
              {allLinks.map((link, index) => (
                <li className="nav-item" role="none" key={link.label}>
                  <a
                    className="nav-link"
                    role="menuitem"
                    tabIndex={activeIndex === index ? 0 : -1}
                    href={link.href}
                    aria-current={link.current ? 'page' : undefined}
                    onFocus={() => setActiveIndex(index)}
                    onClick={() => setMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
              {authenticated ? (
                <li className="nav-item" role="none">
                  <button
                    className="nav-link"
                    role="menuitem"
                    type="button"
                    tabIndex={activeIndex === allLinks.length ? 0 : -1}
                    disabled={signingOut}
                    onFocus={() => setActiveIndex(allLinks.length)}
                    onClick={() => void leave()}
                  >
                    {signingOut ? 'Cerrando sesión…' : 'Cerrar sesión'}
                  </button>
                </li>
              ) : null}
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
}
